import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Package, ShoppingCart, CreditCard, Truck, CheckCircle } from 'lucide-react';

const LandingPageHowItWorks: React.FC = () => {
  const steps = [
    {
      icon: <ClipboardList className="h-8 w-8 text-white" />,
      title: 'Registe-se',
      description: 'Crie a sua conta gratuitamente e aceda ao painel de controlo.'
    },
    {
      icon: <Package className="h-8 w-8 text-white" />,
      title: 'Selecione produtos',
      description: 'Escolha entre mais de 200.000 produtos com margens atrativas.'
    },
    {
      icon: <ShoppingCart className="h-8 w-8 text-white" />,
      title: 'Publique na sua loja',
      description: 'Sincronize automaticamente com a sua loja online ou marketplaces.'
    },
    {
      icon: <CreditCard className="h-8 w-8 text-white" />,
      title: 'Receba pedidos',
      description: 'Os pedidos são automaticamente registados no seu painel.'
    },
    {
      icon: <Truck className="h-8 w-8 text-white" />,
      title: 'Nós enviamos',
      description: 'Tratamos do envio diretamente para o seu cliente.'
    },
    {
      icon: <CheckCircle className="h-8 w-8 text-white" />,
      title: 'Receba o seu lucro',
      description: 'Receba o pagamento com a sua margem de lucro já incluída.'
    }
  ];

  const benefits = [
    {
      title: 'Sem investimento inicial',
      description: 'Comece a vender sem comprar stock. Só paga pelos produtos que vender.'
    },
    {
      title: 'Integração com a sua loja',
      description: 'Compatível com WooCommerce, Shopify, Wix, Prestashop e outros.'
    },
    {
      title: 'Acesso a marketplaces',
      description: 'Venda facilmente na Amazon, Worten, FNAC e outros marketplaces.'
    },
    {
      title: 'Gestão centralizada',
      description: 'Gerencie todos os seus pedidos num único painel de controlo.'
    },
    {
      title: 'Envio automático',
      description: 'Nós tratamos de todo o processo de envio para os seus clientes.'
    },
    {
      title: 'Suporte dedicado',
      description: 'Equipa de suporte disponível para ajudar em todas as etapas.'
    }
  ];

  return (
    <div className="py-16 px-4 md:px-10 max-w-7xl mx-auto">
      {/* Como funciona */}
      <div className="text-center mb-16">
        <div className="inline-block mb-6 px-4 py-1 bg-[rgba(140,198,63,0.2)] rounded-full">
          <p className="text-[#8cc63f] font-semibold">COMO FUNCIONA</p>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
          Comece a vender em 6 passos simples
        </h2>
        <p className="text-xl max-w-4xl mx-auto mb-8 text-white/80">
          O nosso sistema foi desenhado para ser simples e eficiente, permitindo que comece a vender rapidamente
        </p>
        
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl max-w-4xl mx-auto mb-12 border border-[#8cc63f]/30">
          <h3 className="text-xl font-bold text-white mb-3">Duas opções para vender em marketplaces:</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div className="bg-white/10 p-4 rounded-lg">
              <h4 className="font-bold text-[#8cc63f] mb-2">1. Venda sem stock (Dropshipping)</h4>
              <p className="text-white/80 text-sm">
                Ideal para quem quer começar sem investimento. Venda produtos do nosso catálogo na sua loja online.
              </p>
            </div>
            
            <div className="bg-white/10 p-4 rounded-lg">
              <h4 className="font-bold text-[#8cc63f] mb-2">2. Venda em marketplaces</h4>
              <p className="text-white/80 text-sm">
                Para vender em marketplaces como Amazon, Worten e FNAC, os produtos precisam estar fisicamente no nosso armazém.
              </p>
            </div>
          </div>
          
          <div className="bg-[rgba(140,198,63,0.1)] p-4 rounded-lg mt-4">
            <p className="text-white/90">
              <strong>Prep Warehouse Fulfillment:</strong> Se já tem stock, basta enviar os seus produtos para o nosso armazém. Nós sincronizamos com todos os marketplaces e tratamos de tudo, sem necessidade de pagar mensalidades aos marketplaces. Cobramos apenas 15% (valor médio cobrado pelos marketplaces) + 5% de comissão por venda.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-[#8cc63f]/50 transition-all">
              <div className="bg-[#8cc63f] rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                {step.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
              <p className="text-white/70">{step.description}</p>
              <div className="mt-4 text-3xl font-bold text-[#8cc63f]">{index + 1}</div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <Link to="/register" className="inline-block px-8 py-4 rounded-lg bg-[#8cc63f] text-white hover:bg-[#7ab52f] transition-colors text-xl font-bold transform hover:scale-105 transition-transform shadow-lg">
            COMEÇAR AGORA
          </Link>
        </div>
      </div>

      {/* Parceria SHIPPIX */}
      <div className="text-center mb-16">
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl max-w-4xl mx-auto border border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-4">
            <div className="text-left">
              <h3 className="text-2xl font-bold text-white mb-3">Envios a partir de 3,90€</h3>
              <p className="text-white/80">
                Em parceria com a <strong className="text-white">SHIPPIX</strong>, oferecemos envios a partir de 3,90€ até 2KG para Portugal continental.
                Utilize o nosso painel para fazer envios mesmo que não estejam relacionados com a plataforma.
              </p>
            </div>
            <img src="/logo_shippix_branco.png" alt="SHIPPIX" className="h-12 object-contain" />
          </div>
        </div>
      </div>

      {/* Benefícios */}
      <div className="text-center mb-16">
        <div className="inline-block mb-6 px-4 py-1 bg-[rgba(140,198,63,0.2)] rounded-full">
          <p className="text-[#8cc63f] font-semibold">BENEFÍCIOS</p>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
          Porque escolher a nossa plataforma
        </h2>
        <p className="text-xl max-w-4xl mx-auto mb-12 text-white/80">
          Oferecemos uma solução completa para quem quer vender online sem complicações
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-[#8cc63f]/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
              <p className="text-white/70">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Final */}
      <div className="bg-gradient-to-r from-[rgba(140,198,63,0.3)] to-[rgba(140,198,63,0.1)] p-8 rounded-lg max-w-4xl mx-auto mb-12 shadow-lg">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <h3 className="text-2xl font-bold text-white mb-2">Pronto para começar?</h3>
            <p className="text-white/80 mb-0">
              Registe-se hoje e comece a vender em menos de 24 horas!
            </p>
          </div>
          <Link to="/register" className="whitespace-nowrap px-6 py-3 bg-white text-[#0a192f] rounded-lg font-bold hover:bg-[#8cc63f] hover:text-white transition-colors">
            CRIAR CONTA GRÁTIS
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LandingPageHowItWorks;
